import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2, Bell } from "lucide-react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// Notification channel matrix (In-app / Telegram) for every role. The Telegram integration
// itself (bot token, chat, delivery logs) lives in Settings → Security.
export function NotificationsCenter() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["telegram"], queryFn: async () => (await http.get("/telegram")).data });
  const integration = data?.integration;
  const events = data?.available_events || [];
  const connected = !!integration?.connected;

  const [inApp, setInApp] = useState(new Set());
  const [tg, setTg] = useState(new Set());
  const [savingCh, setSavingCh] = useState(false);

  useEffect(() => {
    if (data) {
      setInApp(new Set(data.in_app || []));
      setTg(new Set(integration?.events || []));
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (setter) => (k) => setter((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const saveChannels = async () => {
    setSavingCh(true);
    try {
      await http.post("/telegram/channels", { in_app: [...inApp], telegram: [...tg] });
      qc.invalidateQueries({ queryKey: ["telegram"] });
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save preferences"); }
    finally { setSavingCh(false); }
  };

  if (isLoading) return <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-10 text-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="notifications-center">
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
        <div className="mb-1 flex items-center gap-2"><Bell className="h-4 w-4 text-foreground" /><h3 className="text-sm font-medium text-foreground">Notification channels</h3></div>
        <p className="mb-4 text-sm text-muted-foreground">Choose how you want to be notified for each event.</p>
        <div className="overflow-hidden rounded-lg border border-white/[0.06]">
          <table className="w-full" data-testid="channel-matrix">
            <thead className="border-b border-white/[0.06] bg-white/[0.02]">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">Event</th>
                <th className="w-24 px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">In-app</th>
                <th className="w-24 px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">Telegram</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {events.map((e) => (
                <tr key={e.key} className="hover:bg-white/[0.02]" data-testid={`event-row-${e.key}`}>
                  <td className="px-3 py-2.5 text-sm text-foreground/90">
                    {e.label}
                    {e.telegram_only && <span className="ml-2 rounded-full bg-[#38BDF8]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#38BDF8]">Telegram only</span>}
                    {e.future && !e.telegram_only && <span className="ml-2 rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">soon</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {e.telegram_only ? (
                      <span className="inline-flex h-5 w-9 items-center justify-center rounded-full bg-white/[0.04] text-[10px] text-muted-foreground/60" data-testid={`inapp-${e.key}-disabled`}>—</span>
                    ) : (
                      <Switch checked={inApp.has(e.key)} onCheckedChange={() => toggle(setInApp)(e.key)} data-testid={`inapp-${e.key}`} />
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center"><Switch checked={tg.has(e.key)} disabled={!connected} onCheckedChange={() => toggle(setTg)(e.key)} data-testid={`tg-${e.key}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!connected && <p className="mt-2 text-xs text-muted-foreground/70" data-testid="tg-connect-hint">Connect Telegram in Settings → Security to enable the Telegram channel.</p>}
        <Button onClick={saveChannels} disabled={savingCh} data-testid="save-channels" className="mt-4 gap-1.5 bg-white text-black hover:bg-white/90">
          {savingCh ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save preferences
        </Button>
      </div>
    </div>
  );
}
